import React from 'react';
import { motion } from 'motion/react';
import { 
  Zap, MapPin, TrendingUp, Users, 
  ShieldCheck, Mic, ShoppingBag, 
  ArrowRight, CreditCard, Sparkles,
  Phone, Lightbulb, Car
} from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';

const images = {
  hero: "https://images.unsplash.com/photo-1574612357719-5dd0a272afe2?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxidXN5JTIwbGFnb3MlMjBuaWdlcmlhJTIwdHJhZmZpYyUyMHN0cmVldCUyMGxpZmV8ZW58MXx8fHwxNzcxNzI3NDc1fDA&ixlib=rb-4.1.0&q=80&w=1080",
  user: "https://images.unsplash.com/photo-1677935711525-1059c0cb9b44?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxuaWdlcmlhbiUyMHlvdW5nJTIwYWR1bHQlMjBzbWlsaW5nJTIwd2l0aCUyMHNtYXJ0cGhvbmV8ZW58MXx8fHwxNzcxNzI3NDc1fDA",
  community: "https://images.unsplash.com/photo-1734255026082-82fdc81991f0?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb21tdW5pdHklMjBnYXRoZXJpbmclMjBuaWdlcmlhJTIwbmVpZ2hib3Job29kfGVufDF8fHx8MTc3MTcyNzQ3NXww",
  wallet: "https://images.unsplash.com/photo-1595079676339-1534801ad6cf?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBkaWdpdGFsJTIwd2FsbGV0JTIwbW9iaWxlJTIwYXBwJTIwaW50ZXJmYWNlfGVufDF8fHx8MTc3MTcyNzQ3NXww"
};

export function AdvertTemplate() {
  return (
    <div className="min-h-screen bg-zinc-50 font-sans text-zinc-900 selection:bg-green-100 pb-24">
      {/* Dynamic Navbar */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-zinc-100 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-zinc-900 rounded-lg flex items-center justify-center text-white">
            <MapPin className="w-5 h-5" />
          </div>
          <span className="text-xl font-black italic tracking-tighter uppercase">Sentlo</span>
        </div>
        <div className="flex items-center gap-4">
          <button className="text-[10px] font-black uppercase tracking-widest text-zinc-400">Features</button>
          <button className="bg-green-600 text-white px-5 py-2 rounded-full text-[10px] font-black uppercase tracking-widest shadow-lg shadow-green-200">
            Download
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="relative p-6 pt-12 max-w-4xl mx-auto overflow-hidden">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="relative z-10"
        >
          <div className="inline-flex items-center gap-2 bg-zinc-900 text-white px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-[0.2em] mb-6">
            <Sparkles className="w-3 h-3 text-green-400" />
            Recently Rebranded from Sendlo
          </div>
          <h1 className="text-5xl md:text-7xl font-black italic uppercase tracking-tighter leading-[0.9] mb-6">
            KNOW YOUR <br />
            <span className="text-green-600">STREETS</span> <br />
            GET REWARDED.
          </h1>
          <p className="text-base font-bold text-zinc-500 max-w-sm leading-relaxed mb-8">
            The community pulse for Lagos & Abuja. Traffic updates, power alerts, and local shopping—all in one app.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <button className="bg-green-600 text-white h-16 px-10 rounded-[32px] flex items-center justify-center gap-3 shadow-xl shadow-green-200 transform active:scale-95 transition-all">
              <span className="text-sm font-black uppercase tracking-widest">Share Update Now</span>
              <ArrowRight className="w-5 h-5" />
            </button>
            <button className="bg-white border-2 border-zinc-900 h-16 px-10 rounded-[32px] flex items-center justify-center gap-3 transform active:scale-95 transition-all">
              <span className="text-sm font-black uppercase tracking-widest text-zinc-900">Earn Sentlo Coins</span>
            </button>
          </div>
        </motion.div>

        <div className="mt-12 rounded-[48px] overflow-hidden aspect-[16/10] relative shadow-2xl">
          <ImageWithFallback src={images.hero} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          <div className="absolute bottom-8 left-8 right-8 flex items-center justify-between text-white">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-white shadow-lg">
                <ImageWithFallback src={images.user} />
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-tight">"Heavy Go-Slow at Ikeja!"</p>
                <p className="text-[10px] opacity-60 font-bold">REPORTED 2 MINS AGO BY TUNJI</p>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-white/20 backdrop-blur-md px-4 py-2 rounded-2xl border border-white/20">
              <Zap className="w-4 h-4 text-green-400 fill-green-400" />
              <span className="text-xs font-black">+5 COINS</span>
            </div>
          </div>
        </div>
      </header>

      {/* Feature Grid */}
      <section className="p-6 max-w-4xl mx-auto space-y-12 mt-12">
        <div className="text-center space-y-2">
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-green-600">Core Features</p>
          <h2 className="text-4xl font-black italic uppercase tracking-tighter">Everything Nigeria Needs.</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Power Watch */}
          <div className="bg-zinc-900 rounded-[40px] p-8 text-white relative overflow-hidden group">
            <div className="relative z-10 flex flex-col h-full">
              <div className="w-14 h-14 bg-green-500 rounded-3xl flex items-center justify-center text-zinc-900 mb-6 group-hover:rotate-12 transition-transform">
                <Lightbulb className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-black italic uppercase tracking-tighter mb-4 leading-none">Sentlo Light Watch</h3>
              <p className="text-sm text-zinc-400 font-medium mb-8 leading-relaxed">
                Stop guessing if PHCN is back. Real-time neighborhood power tracking from people on your street.
              </p>
              <div className="mt-auto">
                <span className="text-[10px] font-black uppercase tracking-[0.2em] px-4 py-2 border border-white/20 rounded-full">Active Monitoring</span>
              </div>
            </div>
            <div className="absolute -right-8 -bottom-8 opacity-10 group-hover:opacity-20 transition-opacity">
              <Zap className="w-48 h-48" />
            </div>
          </div>

          {/* Social Saving */}
          <div className="bg-white border border-zinc-100 rounded-[40px] p-8 text-zinc-900 relative overflow-hidden group shadow-sm hover:shadow-xl transition-shadow">
            <div className="relative z-10 flex flex-col h-full">
              <div className="w-14 h-14 bg-zinc-100 rounded-3xl flex items-center justify-center text-zinc-900 mb-6">
                <Users className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-black italic uppercase tracking-tighter mb-4 leading-none">Digital Ajo Circles</h3>
              <p className="text-sm text-zinc-500 font-medium mb-8 leading-relaxed">
                Save with friends you trust. Modern Esusu with automated collections and Sentlo SafePay Escrow.
              </p>
              <div className="mt-auto">
                <span className="text-[10px] font-black uppercase tracking-[0.2em] px-4 py-2 bg-green-50 text-green-600 rounded-full">Trust Based</span>
              </div>
            </div>
          </div>

          {/* Market Price */}
          <div className="bg-zinc-100 rounded-[40px] p-8 text-zinc-900 relative overflow-hidden group md:col-span-2">
            <div className="flex flex-col md:flex-row gap-12 items-center">
              <div className="flex-1 space-y-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-white rounded-2xl shadow-sm">
                    <TrendingUp className="w-6 h-6 text-zinc-900" />
                  </div>
                  <h3 className="text-2xl font-black italic uppercase tracking-tighter leading-none">Market Basket Index</h3>
                </div>
                <p className="text-sm text-zinc-500 font-medium leading-relaxed">
                  Real-time price tracking for food and essentials. Know exactly how much bread, rice, and fuel costs at Mile 12 or Garki Market today.
                </p>
                <div className="flex gap-4">
                  <div className="bg-white p-4 rounded-[24px] shadow-sm flex flex-col items-center flex-1">
                    <span className="text-[8px] font-black text-zinc-400 uppercase mb-1">Fuel Price</span>
                    <span className="text-lg font-black tracking-tight text-zinc-900">₦650/L</span>
                  </div>
                  <div className="bg-white p-4 rounded-[24px] shadow-sm flex flex-col items-center flex-1">
                    <span className="text-[8px] font-black text-zinc-400 uppercase mb-1">Bread (Big)</span>
                    <span className="text-lg font-black tracking-tight text-zinc-900">₦1,200</span>
                  </div>
                </div>
              </div>
              <div className="flex-1 w-full h-48 md:h-64 rounded-[32px] overflow-hidden shadow-lg transform rotate-2">
                <ImageWithFallback src={images.wallet} className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rewards Section */}
      <section className="bg-green-600 mt-24 py-24 px-6 relative overflow-hidden">
        <div className="max-w-4xl mx-auto text-center space-y-8 relative z-10">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md px-6 py-2 rounded-full border border-white/20 text-white">
            <Sparkles className="w-4 h-4" />
            <span className="text-xs font-black uppercase tracking-widest">Sentlo Rewards</span>
          </div>
          <h2 className="text-5xl md:text-7xl font-black italic uppercase tracking-tighter text-white leading-[0.9]">
            YOUR VOICE <br />
            IS VALUE.
          </h2>
          <p className="text-lg text-green-100 font-bold max-w-xl mx-auto leading-relaxed">
            Convert 50 Sentlo Coins into ₦100 worth of Mobile Airtime or Electricity instantly. Reporting helps everyone—including your wallet.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <div className="bg-white/10 backdrop-blur-md p-8 rounded-[40px] border border-white/20 text-white min-w-[240px]">
              <span className="text-4xl font-black tracking-tighter">50</span>
              <span className="text-sm font-bold ml-2">COINS</span>
              <div className="h-px w-full bg-white/20 my-4" />
              <span className="text-2xl font-black text-green-300 italic uppercase">₦100</span>
              <span className="text-[10px] block opacity-60 font-black uppercase mt-1 tracking-widest">Airtime / Light</span>
            </div>
            <button className="bg-white text-zinc-900 h-16 px-12 rounded-full text-sm font-black uppercase tracking-widest shadow-2xl transform active:scale-95 transition-all">
              Start Earning Now
            </button>
          </div>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
          <Zap className="absolute top-10 left-10 w-32 h-32 text-white animate-pulse" />
          <MapPin className="absolute bottom-10 right-10 w-24 h-24 text-white animate-pulse" />
          <Mic className="absolute top-1/2 left-20 w-16 h-16 text-white rotate-12" />
        </div>
      </section>

      {/* Trust & Safety */}
      <section className="p-12 max-w-4xl mx-auto">
        <div className="bg-zinc-50 border border-zinc-200 rounded-[48px] p-8 md:p-16 flex flex-col md:flex-row gap-12 items-center">
          <div className="w-32 h-32 bg-zinc-900 rounded-[40px] flex items-center justify-center text-green-400 shrink-0 shadow-xl shadow-zinc-200">
            <ShieldCheck className="w-16 h-16" />
          </div>
          <div className="space-y-4 text-center md:text-left">
            <h3 className="text-3xl font-black italic uppercase tracking-tighter leading-none">Sentlo AI Shield</h3>
            <p className="text-base text-zinc-500 font-medium leading-relaxed">
              Every report is scanned by our AI Shield to prevent spam, misinformation, and privacy violations. Real-time bank-grade security for your data, protected by Neighbor Trust Scores.
            </p>
            <div className="flex flex-wrap justify-center md:justify-start gap-4 pt-4">
              {['Biometric Auth', 'AES-256 Encryption', 'SafePay Escrow'].map((item) => (
                <span key={item} className="text-[10px] font-black uppercase tracking-widest text-zinc-400 border border-zinc-200 px-4 py-2 rounded-full">
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Footer / Final CTA */}
      <footer className="p-12 text-center space-y-12">
        <div className="max-w-2xl mx-auto space-y-6">
          <h2 className="text-5xl font-black italic uppercase tracking-tighter leading-none">JOIN THE SENTLO COMMUNITY</h2>
          <p className="text-sm text-zinc-500 font-bold uppercase tracking-widest">Lagos • Abuja • Port Harcourt • Kano</p>
        </div>
        
        <div className="flex justify-center gap-8">
          <div className="flex flex-col items-center gap-2">
            <div className="w-12 h-12 rounded-2xl bg-zinc-100 flex items-center justify-center text-zinc-900">
              <Phone className="w-6 h-6" />
            </div>
            <span className="text-[10px] font-black uppercase tracking-widest text-zinc-400">iOS App</span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <div className="w-12 h-12 rounded-2xl bg-zinc-100 flex items-center justify-center text-zinc-900">
              <Smartphone className="w-6 h-6" />
            </div>
            <span className="text-[10px] font-black uppercase tracking-widest text-zinc-400">Android</span>
          </div>
        </div>

        <div className="pt-12 border-t border-zinc-100">
          <div className="flex items-center justify-center gap-2 opacity-30 grayscale mb-6">
            <div className="w-6 h-6 bg-zinc-900 rounded flex items-center justify-center text-white">
              <MapPin className="w-3 h-3" />
            </div>
            <span className="text-xs font-black italic tracking-tighter uppercase">Sentlo</span>
          </div>
          <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">© 2026 Sentlo Inc. All Rights Reserved.</p>
        </div>
      </footer>

      {/* Floating Call to Action */}
      <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[45] w-[calc(100%-48px)] max-w-md">
        <div className="bg-white/90 backdrop-blur-xl border border-white shadow-2xl rounded-[32px] p-4 flex items-center gap-4">
          <div className="w-12 h-12 bg-green-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-green-200 shrink-0">
            <Car className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-black text-green-600 uppercase tracking-widest leading-none mb-1">Live Update</p>
            <p className="text-xs font-black text-zinc-900 truncate">Go-slow at Third Mainland Bridge</p>
          </div>
          <button className="bg-zinc-900 text-white px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transform active:scale-95 transition-all">
            Join
          </button>
        </div>
      </div>
    </div>
  );
}